
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Destination {
  title: string;
  duration: string;
  description: string[];
  image: string;
  buttonText: string;
  buttonAction: string;
}

const destinations: Destination[] = [
  {
    title: "Valle de Cocora, Colombia",
    duration: "Austria Country Side - Tour from 10 Days to 21 Days",
    description: [
      "Home to the world's tallest wax palm trees, this surreal valley offers misty hikes through cloud forests and traditional coffee villages.",
      "It's a paradise for eco-travelers."
    ],
    image: "/images/south-america-dest-1.jpg",
    buttonText: "Explore More",
    buttonAction: "explore"
  },
  {
    title: "Lençóis Maranhenses, Brazil",
    duration: "Experience Brazil With BluOrb",
    description: [
      "Otherworldly white sand dunes filled with turquoise lagoons during the rainy season.",
      "A rare mix of desert and oasis landscapes unlike anywhere else in the world."
    ],
    image: "/images/south-america-dest-2.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Chachapoyas & Kuelap, Peru",
    duration: "Explore Peru With PrimeMeridian",
    description: [
      "Nicknamed the “Machu Picchu of the North,” Kuelap is a massive fortress built by the Chachapoya culture. Less crowded but equally fascinating, paired with stunning waterfalls like Gocta."
    ],
    image: "/images/south-america-dest-3.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Isla de la Plata, Ecuador",
    duration: "Experience Brazin With BluOrb",
    description: [
      "Known as the \"Poor Man’s Galápagos,\" this island offers whale-watching,",
      "Blue-footed boobies, and pristine trails — without the high Galápagos price tag."
    ],
    image: "/images/south-america-dest-4.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Bariloche & the Seven Lakes, Argentina",
    duration: "Explore Argentina 10 Night / 11 Days",
    description: [
      "Patagonia’s alpine heart, filled with Swiss-style towns, chocolate shops, and glacial lakes that glow in shades of emerald and blue."
    ],
    image: "/images/south-america-dest-5.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Potosí & Cerro Rico, Bolivia",
    duration: "Explore Bolivia 10 Night / 11 Days",
    description: [
      "A historic silver-mining city that once funded the Spanish Empire. Visitors can explore colonial streets and even descend into the mines with local guides.",
    ],
    image: "/images/south-america-dest-6.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Península Valdés, Argentina",
    duration: "Explore Argentina 10 Night / 11 Days",
    description: [
      "A UNESCO site where visitors can spot orcas, sea lions, penguins, and migrating whales.",
      "It's one of the world's best marine wildlife destinations."
    ],
    image: "/images/south-america-dest-7.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "San Pedro de Atacama, Chile",
    duration: "Explore Chile 10 Night / 11 Days",
    description: [
      "A surreal desert with salt flats, geysers, lunar valleys, and some of the clearest stargazing skies on earth.",
      "Perfect for astronomy lovers."
    ],
    image: "/images/south-america-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Isla del Sol, Bolivia (Lake Titicaca)",
    duration: "Explore Around Isla del Sol 10 Night / 11 Days",
    description: [
      "Steeped in Incan mythology, this tranquil island offers hiking trails, terraced hillsides, and sacred ruins",
      "Far from the bustle of mainland cities."
    ],
    image: "/images/south-america-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Punta Gallinas, Colombia",
    duration: "Punta Gallinas and around 10 Night / 11 Days",
    description: [
      "The northernmost point of South America, where desert sand dunes meet the Caribbean Sea.",
      "A remote, culturally rich experience in Wayuu indigenous territory."
    ],
    image: "/images/south-america-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  }
];

export const DestinationsSection: React.FC = () => {
  const navigate = useNavigate();

  const handleButtonClick = (action: string, title: string) => {
    if (action === 'enquire') {
      navigate(`/contact?subject=${encodeURIComponent('Enquiry about ' + title)}`);
      return;
    }

    if (action === 'explore') {
      navigate('/group-tours');
      return;
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Must See Sightseeing in South American Region
          </h2>
        </div>

        <div className="space-y-16">
          {destinations.map((destination, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
            >
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {destination.title}
                </h3>
                <p className="text-lg text-emerald-600 font-medium">
                  {destination.duration}
                </p>
                <ul className="space-y-3">
                  {destination.description.map((desc, descIndex) => (
                    <li key={descIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleButtonClick(destination.buttonAction, destination.title)}
                  className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {destination.buttonText}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-2"></i>
                </button>
              </div>

              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <img
                  src={destination.image}
                  alt={destination.title}
                  className="w-full h-80 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
